import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { formatDateTime } from 'utils/date-helpers';
import styled from 'styled-components';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { StandbyFTDTO } from 'api/model';
import { makeStringComparator } from 'utils/sorting';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Routes from 'constants/Routes';
import useModalStack from 'contexts/modal/useModalStack';
import DetailedCaseFormProvider from '../../../components/DetailedCase/DetailedCaseFormProvider';
import DetailedCaseProvider from '../../../components/DetailedCase/DetailedCaseProvider';
import { CaseModal } from 'pages/Case';
import {
  faArrowUpRightFromSquare,
  faCheckCircle,
  faCircleXmark,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import TextButton from 'components/inputs/TextButton';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import useUsers from 'contexts/basicData/useUsers';
import { useTransportLogisticsGetStandbyFTDTOs } from 'api/transport-logistics/transport-logistics';
import { getTableRowClassName } from 'components/Dashboard/table-helpers';

const TABLE_NAME = 'FTs-on-standby';

const RegnrWrapper = styled.div`
  display: flex;
  gap: 5px;
  flex-direction: row;
  flex-wrap: wrap;
`;

const MyTextButton = styled(TextButton)`
  cursor: pointer;
  max-height: 15px;
  padding: 0px;
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin-right: 5px;
`;

const FTsOnStandbyWidget = () => {
  const [standbyFTs, setStandbyFTs] = useState<StandbyFTDTO[]>([]);
  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();
  const users = useUsers();
  const modalStack = useModalStack();

  const getStandbyFTs = useTransportLogisticsGetStandbyFTDTOs(
    {
      startDate: daterange[0] ?? undefined,
      endDate: daterange[1] ?? undefined,
      businessUnitIDs: selectedBusinessUnits,
    },
    {
      query: {
        refetchInterval: UPDATE_INTERVAL,
      },
    }
  );

  useEffect(() => {
    if (getStandbyFTs.data) {
      setStandbyFTs(getStandbyFTs.data as StandbyFTDTO[]);
    } else {
      setStandbyFTs([] as StandbyFTDTO[]);
    }
  }, [getStandbyFTs.data]);

  const filteredComments = useMemo(() => {
    if (!searchText.length) {
      return standbyFTs;
    }
    return standbyFTs
      ? standbyFTs.filter((c) => {
          return c.userName?.toLowerCase().includes(searchText.toLowerCase());
        })
      : [];
  }, [standbyFTs, searchText]);

  const columnSettings: ColumnSetting<StandbyFTDTO, any>[] = useMemo(() => {
    const handleCaseClick = (caseID: number) => (e: React.MouseEvent) => {
      if (e.ctrlKey || e.metaKey) {
        window.open(
          `${Routes.search.index}/${caseID}`,
          caseID.toString() ?? '_blank'
        );
      } else {
        modalStack.push(
          <DetailedCaseProvider caseId={caseID}>
            <DetailedCaseFormProvider>
              <CaseModal onClose={() => modalStack.pop()} />
            </DetailedCaseFormProvider>
          </DetailedCaseProvider>
        );
      }
    };

    const columns: ColumnSetting<StandbyFTDTO, any>[] = [
      {
        head: 'Nr',
        render: (e, t, f, i) => i + 1,
        width: 15,
      },
      {
        head: 'Namn',
        render: (row) => `${row.userName}`,
        width: 50,
        sortFunction: makeStringComparator((row) => row.userName),
      },
      {
        head: 'Telefonnummer',
        render: (row) => {
          return (
            <span>
              {row.userId && users[row.userId]
                ? users[row.userId].phoneNumber
                : ''}
            </span>
          );
        },
        width: 70,
        sortFunction: (a, b) =>
          (users[a.userId]?.phoneNumber ?? '').localeCompare(
            users[b.userId]?.phoneNumber ?? ''
          ),
      },
      {
        head: 'Tid',
        render: (row) =>
          `${row.standbyStart ? formatDateTime(row.standbyStart) : '-'} – ${row.standbyEnd ? formatDateTime(row.standbyEnd) : '-'}`,
        width: 100,
        sortFunction: makeStringComparator(
          (row) =>
            `${row.standbyStart ? formatDateTime(row.standbyStart) : '-'} – ${row.standbyEnd ? formatDateTime(row.standbyEnd) : '-'}`
        ),
      },
      {
        head: 'Standby-tid berättigad',
        render: (row) => (
          <RegnrWrapper>
            {row.standbyStatus.map((c) => (
              <MyTextButton
                key={c.caseID}
                onClick={handleCaseClick(c.caseID)}
                title={
                  c.isStandbyEligible === true
                    ? 'Standby-tid berättigad'
                    : c.isStandbyEligible === false
                      ? 'Standby-tid inte berättigad'
                      : 'Berättigande inte satt'
                }
              >
                {c.registrationNumber}
                {c.isStandbyEligible === true && (
                  <FontAwesomeIcon fontSize="18px" icon={faCheckCircle} />
                )}
                {c.isStandbyEligible === false && (
                  <FontAwesomeIcon fontSize="18px" icon={faCircleXmark} />
                )}
                {c.isStandbyEligible === null && (
                  <FontAwesomeIcon fontSize="18px" icon={faQuestionCircle} />
                )}
              </MyTextButton>
            ))}
          </RegnrWrapper>
        ),
        width: 100,
      },
      {
        head: 'Avbokade körningar utan ersättningsuppdrag',
        render: (row) => (
          <RegnrWrapper>
            {row.originatingCases.map((c) => (
              <MyTextButton key={c.caseID} onClick={handleCaseClick(c.caseID)}>
                {c.registrationNumber}
                <FontAwesomeIcon
                  fontSize="18px"
                  icon={faArrowUpRightFromSquare}
                />
              </MyTextButton>
            ))}
          </RegnrWrapper>
        ),
        width: 100,
      },
    ];
    return columns;
  }, [modalStack, users]);
  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.FTsOnStandby]}
      count={standbyFTs.length}
      isLoading={getStandbyFTs.isLoading}
      info="Fälttestare som är på standby och har avbokade körningar utan ersättningsuppdrag."
    >
      <Table
        columnSettings={columnSettings}
        rows={filteredComments}
        rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.userId)}
        useColumnWidthAsFlexAmount
      />
    </Widget>
  );
};

export default FTsOnStandbyWidget;
